/**
 * Contact Detail Page
 * Master Data > Contact detail view (matching CategoryDetailPage style)
 */

import { useState } from 'react';
import {
  Button,
  Breadcrumb,
  Typography,
  theme,
  Spin,
  App,
  Card,
  Divider,
  Switch,
  Tag,
  Descriptions,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useMasterDataContactDetail,
  useMasterDataContactMutations,
} from '@/hooks/useMasterDataContacts';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useElevation } from '@/styles/useElevation';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import MasterDataContactFormModal from './MasterDataContactFormModal';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import type { ContactType, MasterDataContactDetail } from '@/types/api/masterDataContact';
import { normalizeContactAffiliations } from '@/types/api/masterDataContact';
import { formatPhoneFromApiForDisplay } from '@/utils/phoneUtils';
import { ContactAddressDisplay } from '@/components/common/ContactAddressDisplay';
import { TimestampPair } from '@/components/common/TimestampDisplay';
import PageContainer from '@/components/common/PageContainer';

const { Text } = Typography;

const ContactDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { token } = theme.useToken();
  const elevation = useElevation();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { t } = useVendorTranslation();
  const { t: tContacts } = useTranslation('contacts');

  const contactId = id ? parseInt(id) : null;
  const entityId = searchParams.get('entityId')
    ? parseInt(searchParams.get('entityId')!)
    : null;
  const type = searchParams.get('type')
    ? (parseInt(searchParams.get('type')!) as ContactType)
    : null;

  // Fetch contact details using hook
  const { contact, isLoading, error, refresh } = useMasterDataContactDetail(
    contactId,
    entityId,
    type
  );
  const { toggleVendorContactStatus, toggleCustomerContactStatus } =
    useMasterDataContactMutations();

  const [toggling, setToggling] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);

  // Confirmation dialog for status toggle
  const toggleStatusDialog = useConfirmationDialog<MasterDataContactDetail>();

  // Handle status toggle
  const handleStatusToggle = () => {
    if (!contact) return;
    // Don't allow deactivating primary contact
    if (contact.isPrimary && contact.isActive) return;
    toggleStatusDialog.open(contact);
  };

  // Design settings (matching CategoryDetailPage)
  const currentColorScheme = {
    headerBg: token.colorBgContainer,
    headerAccent: token.colorPrimary,
    cardShadow: token.boxShadow,
    borderColor: token.colorBorderSecondary,
    highlightBg: token.colorPrimaryBg,
  };

  const currentFontSizes = {
    title: token.fontSizeHeading2,
    subtitle: token.fontSize,
    body: token.fontSize,
    label: token.fontSizeSM,
    menuItem: token.fontSizeLG,
  };

  if (isLoading) {
    return (
      <div className="tw-flex tw-items-center tw-justify-center tw-h-screen">
        <Spin size="large" />
      </div>
    );
  }

  if (error || !contact) {
    return (
      <PageContainer>
        <Typography.Text type="danger">
          {error ? tContacts('messages.error', { message: error.message }) : tContacts('messages.contactNotFound')}
        </Typography.Text>
        <div style={{ marginTop: token.marginLG }}>
          <Button onClick={() => navigate('/auth/contacts')}>
            {tContacts('buttons.backToContacts')}
          </Button>
        </div>
      </PageContainer>
    );
  }

  // Determine contact type label
  const contactTypeLabel = contact.vendorId
    ? tContacts('tabs.vendor')
    : tContacts('tabs.customer');

  // #92: every company this person is affiliated to (legacy single-org
  // records normalize to a one-element list)
  const affiliations = normalizeContactAffiliations(contact);

  // Format phone for display
  const phoneDisplay = formatPhoneFromApiForDisplay(
    {
      phoneCountryCode: contact.phoneCountryCode,
      phoneNumber: contact.phoneNumber,
      phoneExtension: contact.phoneExtension,
    },
    t('vendor.fields.ext')
  );

  const mobileDisplay = formatPhoneFromApiForDisplay(
    {
      phoneCountryCode: contact.mobileCountryCode || '',
      phoneNumber: contact.mobileNumber || '',
    },
    t('vendor.fields.ext')
  );

  return (
    <PageContainer>
      {/* Merged Breadcrumb + Header Card */}
      <Card
        className="tw-mb-6"
        style={{
          ...elevation.card,
          background: currentColorScheme.headerBg,
        }}
      >
        {/* Back button + Breadcrumb aligned horizontally */}
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/auth/contacts')}
            style={{
              fontSize: currentFontSizes.body,
              padding: 0,
              height: 'auto',
              lineHeight: 1,
            }}
          >
            {t('vendor.buttons.back')}
          </Button>
          <Breadcrumb
            items={[
              {
                title: t('vendor.breadcrumbs.masterData'),
              },
              {
                title: <a onClick={() => navigate('/auth/contacts')}>{tContacts('breadcrumbs.contact')}</a>,
              },
              {
                title: contact.name,
              },
            ]}
          />
        </div>

        <Divider style={{ margin: '12px 0' }} />

        {/* Header content */}
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-6">
          <div className="tw-flex tw-items-center tw-gap-4 tw-flex-1">
            <div className="tw-flex-1">
              <h1
                className="tw-mb-2"
                style={{
                  fontSize: currentFontSizes.title,
                  fontWeight: token.fontWeightStrong,
                  lineHeight: 1,
                  color: currentColorScheme.headerAccent,
                }}
              >
                {contact.name}
              </h1>
              <p
                style={{
                  fontSize: currentFontSizes.subtitle,
                  color: token.colorTextSecondary,
                  margin: 0,
                }}
              >
                {contact.jobTitle || tContacts('labels.noJobTitle')}
              </p>
            </div>
          </div>

          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => setEditModalVisible(true)}
            disabled={!contact.isActive}
            style={{
              height: `${controlHeightTokens.controlHeightXL}px`,
              fontSize: currentFontSizes.body,
            }}
          >
            {t('vendor.actions.edit')}
          </Button>
        </div>

        <Divider style={{ margin: '16px 0' }} />

        {/* Contact Meta Info - Company, Type, Primary, Status */}
        <style>
          {`
            .company-link:hover {
              text-decoration: underline;
            }
          `}
        </style>
        <div className="tw-flex tw-gap-8 tw-flex-wrap" style={{ paddingTop: '8px' }}>
          {/* Company with Link + Type Tag */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <span style={{ fontSize: currentFontSizes.label, color: token.colorTextSecondary }}>
              {tContacts('fields.company')}:
            </span>
            {contact.companyName ? (
              <>
                <Text
                  className="company-link"
                  style={{
                    fontSize: currentFontSizes.body,
                    fontWeight: token.fontWeightStrong,
                    color: token.colorPrimary,
                    cursor: 'pointer',
                  }}
                  onClick={() => {
                    if (contact.vendorId) {
                      navigate(`/auth/vendors/${contact.vendorId}`);
                    } else if (contact.customerId) {
                      navigate(`/auth/customers/${contact.customerId}`);
                    }
                  }}
                >
                  {contact.companyName}
                </Text>
                <Tag
                  bordered={false}
                  color="purple"
                  style={{
                    marginLeft: 4,
                    borderRadius: radiusTokens.control,
                  }}
                >
                  {contactTypeLabel}
                </Tag>
              </>
            ) : (
              <Text style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
                -
              </Text>
            )}
          </div>

          {/* Primary */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <span style={{ fontSize: currentFontSizes.label, color: token.colorTextSecondary }}>
              {tContacts('fields.primary')}:
            </span>
            <Text style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
              {contact.isPrimary ? t('vendor.fields.yes') : t('vendor.fields.no')}
            </Text>
          </div>

          {/* Timestamps */}
          <TimestampPair
            createdAt={contact.createdAt}
            updatedAt={contact.updatedAt}
            direction="horizontal"
          />

          {/* Status with Switch */}
          <div className="tw-flex tw-items-center tw-gap-2">
            <span style={{ fontSize: currentFontSizes.label, color: token.colorTextSecondary }}>
              {t('vendor.fields.status')}:
            </span>
            <span style={{ fontSize: currentFontSizes.body, fontWeight: token.fontWeightStrong }}>
              {contact.isActive ? t('vendor.status.active') : t('vendor.status.inactive')}
            </span>
            <Switch
              checked={contact.isActive}
              onChange={handleStatusToggle}
              loading={toggling}
              disabled={contact.isPrimary && contact.isActive}
              style={{ transform: 'scale(0.8)' }}
            />
          </div>
        </div>

        {/* Reserved space for Primary tabs area - no divider after this */}
        <div style={{ height: token.sizeMD }} />

        {/* Basic Information - Descriptions style like vendor detail */}
        <div
          style={{
            backgroundColor: token.colorBgContainer,
            overflow: 'hidden',
            ...elevation.card,
          }}
        >
          <Descriptions bordered column={2} size="middle">
            <Descriptions.Item label={tContacts('fields.phone')}>
              {phoneDisplay || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={tContacts('fields.mobile')}>
              {mobileDisplay || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={tContacts('fields.fax')}>
              {contact.fax || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={tContacts('fields.email')}>
              {contact.email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={tContacts('fields.address')} span={2}>
              <ContactAddressDisplay contact={contact} />
            </Descriptions.Item>
            {/* #92: all company affiliations of this ONE person */}
            {affiliations.length > 0 && (
              <Descriptions.Item label={tContacts('fields.affiliations')} span={2}>
                <div className="tw-flex tw-flex-col tw-gap-2">
                  {affiliations.map((affiliation) => (
                    <div
                      key={`${affiliation.companyType}-${affiliation.companyId}`}
                      className="tw-flex tw-items-center tw-gap-2 tw-flex-wrap"
                    >
                      <Tag
                        bordered={false}
                        color={affiliation.companyType === 'vendor' ? 'purple' : 'blue'}
                        style={{ borderRadius: radiusTokens.control }}
                      >
                        {affiliation.companyType === 'vendor'
                          ? tContacts('tabs.vendor')
                          : tContacts('tabs.customer')}
                      </Tag>
                      <Text
                        className="company-link"
                        style={{
                          fontWeight: token.fontWeightStrong,
                          color: token.colorPrimary,
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          navigate(
                            affiliation.companyType === 'vendor'
                              ? `/auth/vendors/${affiliation.companyId}`
                              : `/auth/customers/${affiliation.companyId}`
                          )
                        }
                      >
                        {affiliation.companyName}
                      </Text>
                      {affiliation.roleTitle && (
                        <Text style={{ color: token.colorTextSecondary }}>
                          {affiliation.roleTitle}
                        </Text>
                      )}
                      {affiliation.isPrimary && (
                        <Tag bordered={false} style={{ borderRadius: radiusTokens.control }}>
                          {tContacts('labels.primaryAffiliation')}
                        </Tag>
                      )}
                    </div>
                  ))}
                </div>
              </Descriptions.Item>
            )}
          </Descriptions>
        </div>
      </Card>

      {/* Edit Contact Modal */}
      <MasterDataContactFormModal
        visible={editModalVisible}
        onClose={() => setEditModalVisible(false)}
        onSuccess={() => {
          refresh();
        }}
        contact={contact}
      />

      {/* Confirmation Dialog for Toggle Status */}
      <ConfirmationDialog
        open={toggleStatusDialog.isOpen}
        onClose={toggleStatusDialog.close}
        onConfirm={async () => {
          const contactData = toggleStatusDialog.data;
          if (contactData && contactId && entityId) {
            setToggling(true);
            try {
              const newStatus = !contactData.isActive;
              if (contactData.vendorId) {
                await toggleVendorContactStatus(
                  contactData.vendorId,
                  contactId,
                  newStatus
                );
              } else if (contactData.customerId) {
                await toggleCustomerContactStatus(
                  contactData.customerId,
                  contactId,
                  newStatus
                );
              }
              message.success(
                t('vendor.messages.contactActionSuccess', {
                  action: newStatus
                    ? t('vendor.status.active').toLowerCase()
                    : t('vendor.status.inactive').toLowerCase(),
                })
              );
              await refresh();
              toggleStatusDialog.close();
            } catch (error) {
              message.error(t('vendor.messages.statusUpdateFailed'));
              console.error('Error toggling contact status:', error);
            } finally {
              setToggling(false);
            }
          }
        }}
        title={
          toggleStatusDialog.data?.isActive
            ? tContacts('modals.deactivateContactTitle')
            : tContacts('modals.activateContactTitle')
        }
        message={t('vendor.messages.confirmStatusChange', {
          action: toggleStatusDialog.data?.isActive
            ? t('vendor.actions.deactivate').toLowerCase()
            : t('vendor.actions.activate').toLowerCase(),
          name: toggleStatusDialog.data?.name || '',
        })}
        type={toggleStatusDialog.data?.isActive ? 'danger' : 'info'}
        confirmText={
          toggleStatusDialog.data?.isActive
            ? t('vendor.actions.deactivate')
            : t('vendor.actions.activate')
        }
        loading={toggling}
      />
    </PageContainer>
  );
};

export default ContactDetailPage;
